import { Box, Card, CardContent, CardHeader, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import type { Theme } from "@mui/material/styles"
import type { SalespersonPerformance, MonthlyBreakdown } from "@crm-src/types/financial-metrics"
import { StatsCard } from "@crm-src/components/stats-card"
import { formatCurrency } from "@crm-src/lib/utils"
import { DollarSign, FileText, TrendingUp, Target, Award } from "lucide-react"

interface SalespersonOwnViewProps {
  performance: SalespersonPerformance
  monthlyBreakdown?: MonthlyBreakdown[]
  isLoading?: boolean
}

export function SalespersonOwnView({
  performance,
  monthlyBreakdown,
  isLoading
}: SalespersonOwnViewProps) {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" sx={{ height: 128 }} />
          ))}
        </Box>
        <Skeleton variant="rounded" sx={{ height: 256 }} />
      </Box>
    )
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Welcome Header */}
      <Box
        sx={{
          background: (t: Theme) => `linear-gradient(to right, ${t.palette.primary.light}, ${t.palette.primary.main})`,
          borderRadius: 1,
          p: 3,
          color: "primary.contrastText",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Your Performance</Typography>
            <Typography sx={{ mt: 0.5, opacity: 0.85 }}>Track your sales metrics and achievements</Typography>
          </Box>
          <Box sx={{ display: "flex", opacity: 0.7 }}><Award size={48} /></Box>
        </Box>
      </Box>

      {/* Performance Metrics */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 2 }}>
        <StatsCard
          title="Total Revenue"
          value={formatCurrency(performance.total_revenue, { decimals: 0 })}
          icon={DollarSign}
          iconColor="text-green-600"
          valueColor="text-green-600"
        />

        <StatsCard
          title="Deals Closed"
          value={performance.total_deals_closed}
          icon={Target}
          iconColor="text-blue-600"
          valueColor="text-blue-600"
        />

        <StatsCard
          title="Conversion Rate"
          value={`${performance.conversion_rate}%`}
          icon={TrendingUp}
          iconColor="text-purple-600"
          valueColor="text-purple-600"
        />

        <StatsCard
          title="Pending Quotes"
          value={performance.quotes_pending}
          icon={FileText}
          iconColor="text-orange-600"
          valueColor="text-orange-600"
        />
      </Box>

      {/* Additional Insights */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
        <Card sx={{ borderColor: (t: Theme) => alpha(t.palette.info.main, 0.3), bgcolor: (t: Theme) => alpha(t.palette.info.main, 0.06) }}>
          <CardContent sx={{ pt: 3 }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ fontSize: "1.875rem", fontWeight: 700, color: "info.dark" }}>
                {formatCurrency(performance.average_deal_size, { decimals: 0 })}
              </Typography>
              <Typography sx={{ fontSize: "0.875rem", color: "info.main", mt: 1 }}>Average Deal Size</Typography>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderColor: (t: Theme) => alpha(t.palette.success.main, 0.3), bgcolor: (t: Theme) => alpha(t.palette.success.main, 0.06) }}>
          <CardContent sx={{ pt: 3 }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ fontSize: "1.875rem", fontWeight: 700, color: "success.dark" }}>
                {performance.quotes_converted}
              </Typography>
              <Typography sx={{ fontSize: "0.875rem", color: "success.main", mt: 1 }}>Quotes Converted</Typography>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderColor: (t: Theme) => alpha(t.palette.secondary.main, 0.3), bgcolor: (t: Theme) => alpha(t.palette.secondary.main, 0.08) }}>
          <CardContent sx={{ pt: 3 }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ fontSize: "1.875rem", fontWeight: 700, color: "text.primary" }}>
                {performance.total_quotes_created}
              </Typography>
              <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", mt: 1 }}>Total Quotes Created</Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Monthly Breakdown */}
      {monthlyBreakdown && monthlyBreakdown.length > 0 && (
        <Card>
          <CardHeader title={<Typography variant="h6">Your Monthly Performance</Typography>} />
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Month</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                    <TableCell align="right">Deals Closed</TableCell>
                    <TableCell align="right">Quotes Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {monthlyBreakdown.map((month) => (
                    <TableRow key={month.month}>
                      <TableCell sx={{ fontWeight: 500 }}>{month.month}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: "success.main" }}>
                        {formatCurrency(month.revenue, { decimals: 0 })}
                      </TableCell>
                      <TableCell align="right">{month.deals_closed}</TableCell>
                      <TableCell align="right">{month.quotes_created}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}
